import React from 'react'
import { useSelector } from 'react-redux'
import { Avatar as MuiAvatar } from '@mui/material'

import { PersonIcon } from '~assets/icons'
import { type RootState } from '~redux/rootReducer'
import { useGetIdentityQuery } from '~redux/slices/apiSlice'
import { type Identity0, type Identity1, type Identity2, type IdentityId } from '~types'

interface AvatarProps {
  size: string
  src?: string | undefined
  alt?: string
  backgroundColor?: string | undefined
  identity?: Identity0 | Identity1 | Identity2
  identityId?: IdentityId
  ariaHidden?: boolean
  miniAvatar?: boolean
  style?: React.CSSProperties
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  size,
  backgroundColor,
  identity,
  identityId,
  ariaHidden = false,
  miniAvatar = false,
  style,
}) => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const { data: queryIdentity } = useGetIdentityQuery(
    {
      user_id: userId || '',
      identity_id: identityId,
    },
    { skip: !identityId },
  )
  const identityInfo = queryIdentity?.identity ?? identity

  if (!src && !identityInfo?.image) {
    if (!alt) {
      return (
        <MuiAvatar
          sx={{
            width: size,
            height: size,
            bgcolor: backgroundColor || '#aaa',
            ...style,
          }}
          aria-hidden={ariaHidden}
        >
          <PersonIcon color='#fff' width={parseInt(size) * 0.6} height={parseInt(size) * 0.6} />
        </MuiAvatar>
      )
    }
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: miniAvatar ? '0.5rem' : '0.7rem',
          width: size,
          height: size,
          borderRadius: size,
          backgroundColor: backgroundColor || '#aaa',
          color: '#000',
          ...style,
        }}
        aria-hidden={ariaHidden}
        aria-label={alt ? alt : 'An Avatar'}
      >
        {alt
          ? alt
              .split(/\s+/)
              .map((part) => part.charAt(0).toUpperCase())
              .join('')
          : ''}
      </div>
    )
  }
  if (identityInfo && identityInfo.image) {
    return (
      <img
        src={identityInfo.image}
        alt={identityInfo.name}
        aria-hidden={ariaHidden}
        style={{
          width: size,
          height: size,
          borderRadius: size,
          objectFit: 'cover',
          ...style,
        }}
        aria-label={alt ? alt : identityInfo?.name || 'An Avatar'}
      />
    )
  }
  return (
    <img
      src={src}
      alt={alt}
      aria-hidden={ariaHidden}
      style={{
        width: size,
        height: size,
        borderRadius: size,
        objectFit: 'cover',
        ...style,
      }}
      aria-label={alt ? alt : 'An Avatar'}
    />
  )
}

export default Avatar
